import Big from 'big.js';
import {combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';
import {max, min, numUtil, plus, minus, gt, gte, toString} from '@/helper/num';
import {contracts$, currenciesCurrencyCache$, coinMarginedContractGray$, riskEngineMode$} from '@/config';
import {positions$} from '@/positions/positions';
import {contractAccountsCache$} from './contract-accounts-cache';
import type {ws, CurrencyEntity, Position, ContractPair, ContractAccount} from '@/types';
type ContractAccountRaw = ws.ContractAccountRaw;

export const contractAccounts$ = combineLatest([
  contractAccountsCache$,
  positions$,
  currenciesCurrencyCache$,
  contracts$,
  riskEngineMode$,
  coinMarginedContractGray$,
]).pipe(
  map(([accountsCache, positions, currencies, contracts, riskEngineMode, coinMarginedContractGray]) =>
    accountsCache
      .toArray<ContractAccountRaw>()
      .map((account) =>
        convertAccount(account, positions, currencies, contracts, riskEngineMode, coinMarginedContractGray)
      )
      .filter(Boolean)
  )
);

function calcPositionMarginEv(positions: Position[]) {
  return positions.reduce((p, c) => plus(p, c.marginEv), '0');
}

function calcUnrealisedPnlEv(positions: Position[]) {
  return positions.reduce((p, c) => plus(p, c.unrealisedPnlEvByMarkPrice), '0');
}

function calcUnrealisedPnlEv2(positions: Position[]) {
  return positions.reduce((p, c) => plus(p, c.unrealisedPnlEvByMarkPrice2), '0');
}

function calcFreezeUnrealisedPnlEv(positions: Position[]) {
  return positions
    .filter((o) => o.isCross && o.unrealisedPnlEvByMarkPrice < 0)
    .reduce((p, c) => p + c.unrealisedPnlEvByMarkPrice, 0);
}

function calcOrderMarginEv(positions: Position[]) {
  return positions.reduce((p, c) => plus(p, c.orderCostEv), '0');
}

export function convertAccount(
  account: ContractAccountRaw,
  positions: Position[],
  currenciesCurrencyCache: Map<string, CurrencyEntity>,
  contracts: ContractPair[],
  riskEngineMode: Boolean,
  coinMarginedContractGray: Boolean = false
): ContractAccount {
  const {currency, accountBalanceEv, totalUsedBalanceEv, bonusBalanceEv, isolatedTotalBalanceEv = 0} = account;
  const relatedContracts = contracts.filter(({settleCurrency}) => settleCurrency === currency);
  const relatedPositions = positions.filter((o) => relatedContracts.some(({symbol}) => symbol === o.symbol));
  const relatedCrossPositions = positions.filter(({isCross, size, symbol, currency: currency0}) => {
    if (!isCross || size <= 0 || !relatedContracts.some((x) => x.symbol === symbol)) {
      return false;
    }
    if (currency === 'USDT' && riskEngineMode) {
      return ['USDT', 'MUSDT'].includes(currency0);
    }
    return coinMarginedContractGray ? currency0 === currency : false;
  });
  const unrealisedPnlEv = calcUnrealisedPnlEv(relatedPositions);
  if (!currenciesCurrencyCache.has(currency)) {
    return null;
  }
  const {valueScale: scale, valuePrecision, name, displayCurrency} = currenciesCurrencyCache.get(currency);
  const availableBalanceValuePrecision = /^USD\w$/.test(currency) ? 4 : valuePrecision;
  const valueScale = ['USDC', 'USDT', 'MUSDT', 'PT'].includes(currency) ? 0 : scale;
  const relatedCrossUnPnlEv = calcUnrealisedPnlEv2(relatedCrossPositions);
  const coefficient = gt(relatedCrossUnPnlEv, 0) ? 0.99 : 1.01;
  const discountRiskUnPnlEv = Big(relatedCrossUnPnlEv).times(coefficient);
  const availableAccountBalanceEv = minus(accountBalanceEv, isolatedTotalBalanceEv);
  const abEv = toString(Big(availableAccountBalanceEv).plus(relatedCrossUnPnlEv).minus(totalUsedBalanceEv));
  const abEv2 = toString(Big(availableAccountBalanceEv).plus(discountRiskUnPnlEv).minus(totalUsedBalanceEv));
  const availableBalanceEv = gte(abEv, 0) ? abEv : '0';
  const availableBalanceEv2 = gte(abEv2, 0) ? abEv2 : '0';
  const availableBalance = numUtil.floor(availableBalanceEv, availableBalanceValuePrecision, valueScale);
  const availableBalance2 = numUtil.floor(availableBalanceEv, 2, valueScale);
  const availableBalance4 = numUtil.floor(availableBalanceEv, 4, valueScale);

  const positionMarginEv = calcPositionMarginEv(relatedPositions);
  const orderMarginEv = calcOrderMarginEv(relatedPositions);
  const freezeUnrealisedPnlEv = calcFreezeUnrealisedPnlEv(relatedPositions);
  const marginBalanceEv = plus(accountBalanceEv, freezeUnrealisedPnlEv);
  const equityEv = plus(accountBalanceEv, unrealisedPnlEv);
  const abEvForTransfer = minus(availableBalanceEv2, bonusBalanceEv);
  const availableTransferBalanceEv = min(max(abEvForTransfer, 0), minus(accountBalanceEv, bonusBalanceEv));

  const availableTransferBalance = numUtil.floor(availableTransferBalanceEv, valuePrecision, valueScale);
  const availableTransferBalance2WU = numUtil.floor(availableTransferBalanceEv, 2, valueScale);

  const totalUsedBalanceEv2 = plus(totalUsedBalanceEv, isolatedTotalBalanceEv);
  return {
    name,
    currency,
    accountBalanceEv,
    accountBalance: numUtil.floor(accountBalanceEv, valuePrecision, valueScale),
    totalUsedBalanceEv: totalUsedBalanceEv2,
    totalUsedBalance: numUtil.floor(totalUsedBalanceEv2, valuePrecision, valueScale),
    availableBalanceEv,
    availableBalance,
    availableBalanceWU: availableBalance + ' ' + displayCurrency,
    availableBalance2,
    availableBalance2WU: availableBalance2 + ' ' + displayCurrency,
    availableBalance4,
    availableBalance4WU: availableBalance4 + ' ' + displayCurrency,
    availableTransferBalanceEv,
    availableTransferBalance,
    availableTransferBalanceWU: availableTransferBalance + ' ' + displayCurrency,
    availableTransferBalance2WU: availableTransferBalance2WU + ' ' + displayCurrency,
    bonusBalanceEv,
    bonusBalance: numUtil.floor(bonusBalanceEv, valuePrecision, valueScale),
    positionMarginEv,
    positionMargin: numUtil.floor(positionMarginEv, valuePrecision, valueScale),
    unrealisedPnlEv,
    unrealisedPnl: numUtil.floor(unrealisedPnlEv, valuePrecision, valueScale),
    equityEv,
    equity: numUtil.floor(equityEv, valuePrecision, valueScale),
    marginBalanceEv,
    marginBalance: numUtil.floor(marginBalanceEv, valuePrecision, valueScale),
    orderMarginEv,
    orderMargin: numUtil.floor(orderMarginEv, valuePrecision, valueScale),
  };
}
